import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Tags, X } from 'lucide-react'
import { setCategoryBudgets, type CategoryBudgetPeriod } from '@/api/budget'
import { listCategories, type Category } from '@/api/categories'
import { renameHousehold } from '@/api/households'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { useHousehold } from '@/hooks/useHousehold'
import { track } from '@/lib/analytics'
import { CategoryIcon } from '@/lib/categoryIcon'
import { cn } from '@/lib/utils'
import { FormDialog } from '@/components/transactions/add/fields'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'

const FY_MONTHS: Record<number, string> = { 1: 'Jan – Dec', 4: 'Apr – Mar' }

interface Row {
  period: CategoryBudgetPeriod
  amount: string
}

function initialRow(cat: Category): Row {
  if (cat.yearlyLimit != null) return { period: 'YEARLY', amount: String(cat.yearlyLimit) }
  return { period: 'MONTHLY', amount: cat.monthlyLimit != null ? String(cat.monthlyLimit) : '' }
}

function toAmount(value: string): number | null {
  return value.trim() === '' ? null : Number(value)
}

// An empty amount is "no budget" whichever period is picked, so flipping the switch alone isn't a change.
function changed(before: Row, after: Row): boolean {
  const a = toAmount(before.amount)
  const b = toAmount(after.amount)
  if (a == null && b == null) return false
  return a !== b || before.period !== after.period
}

function PeriodSwitch({
  label,
  value,
  onChange,
}: {
  label: string
  value: CategoryBudgetPeriod
  onChange: (next: CategoryBudgetPeriod) => void
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex shrink-0 overflow-hidden rounded-lg border border-[var(--fl-border)] text-xs"
    >
      {(
        [
          ['MONTHLY', 'Month'],
          ['YEARLY', 'Year'],
        ] as const
      ).map(([period, text]) => (
        <button
          key={period}
          type="button"
          role="radio"
          aria-checked={value === period}
          onClick={() => onChange(period)}
          className={cn(
            'px-2.5 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            value === period
              ? 'bg-[var(--fl-hero-bg)] font-semibold text-[var(--fl-hero-fg)]'
              : 'text-[var(--fl-text-2)] hover:text-[var(--fl-text-1)]'
          )}
        >
          {text}
        </button>
      ))}
    </div>
  )
}

export function EditBudgetDialog({ onClose }: { onClose: () => void }) {
  const { symbol } = useFormatCurrency()
  const { data: household } = useHousehold()
  const { role } = useCurrentHousehold()
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => listCategories(),
  })
  const queryClient = useQueryClient()

  const isOwner = role === 'OWNER'
  const spending = (categoriesData?.categories ?? []).filter((c) => c.type === 'EXPENSE')
  const fyMonths = FY_MONTHS[household?.financialYearStartMonth ?? 4]

  // Null until typed in: shows the household's value, which may arrive after the dialog opens.
  const [budgetInput, setMonthlyBudget] = useState<string | null>(null)
  const [savingsInput, setMonthlySavingsGoal] = useState<string | null>(null)
  const monthlyBudget = budgetInput ?? household?.monthlyBudget?.toString() ?? ''
  const monthlySavingsGoal = savingsInput ?? household?.monthlySavingsGoal?.toString() ?? ''
  const [edits, setEdits] = useState<Record<string, Row>>({})

  function row(cat: Category): Row {
    return edits[cat.id] ?? initialRow(cat)
  }

  function edit(cat: Category, patch: Partial<Row>) {
    setEdits((prev) => ({ ...prev, [cat.id]: { ...row(cat), ...patch } }))
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (isOwner) {
        await renameHousehold({
          monthlyBudget: toAmount(monthlyBudget),
          monthlySavingsGoal: toAmount(monthlySavingsGoal),
        })
      }
      const budgets = spending
        .filter((cat) => cat.id in edits && changed(initialRow(cat), edits[cat.id]))
        .map((cat) => ({
          categoryId: cat.id,
          period: edits[cat.id].period,
          amount: toAmount(edits[cat.id].amount),
        }))
      if (budgets.length > 0) await setCategoryBudgets(budgets)
    },
    onSuccess: async () => {
      if (monthlyBudget.trim() !== '') track('budget_set')
      await queryClient.invalidateQueries({ queryKey: ['household'] })
      await queryClient.invalidateQueries({ queryKey: ['categories'] })
      await queryClient.invalidateQueries({ queryKey: ['budget'] })
      // Setting a monthly budget ticks the onboarding checklist's "set a budget" step.
      await queryClient.invalidateQueries({ queryKey: ['onboarding'] })
      onClose()
    },
  })

  return (
    <FormDialog label="Edit budget" onClose={onClose}>
      <div className="flex items-center justify-between border-b border-[var(--fl-divider)] px-4 py-3">
        <h2 className="font-serif text-lg font-semibold text-[var(--fl-heading)]">Edit budget</h2>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          tooltip="Close"
          onClick={onClose}
          className="text-[var(--fl-text-2)] hover:text-[var(--fl-text-1)]"
        >
          <X size={16} />
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-4">
        {isOwner && (
          <section className="space-y-3">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--fl-text-2)]">
              Household
            </h3>
            <div className="space-y-1">
              <label
                htmlFor="budget-monthly-budget"
                className="block text-sm font-medium text-[var(--fl-text-2)]"
              >
                Monthly spending budget ({symbol})
              </label>
              <Input
                id="budget-monthly-budget"
                type="number"
                min="0"
                step="1"
                value={monthlyBudget}
                onChange={(e) => setMonthlyBudget(e.target.value)}
                placeholder="e.g. 80000"
              />
            </div>
            <div className="space-y-1">
              <label
                htmlFor="budget-monthly-savings-goal"
                className="block text-sm font-medium text-[var(--fl-text-2)]"
              >
                Monthly savings goal ({symbol})
              </label>
              <Input
                id="budget-monthly-savings-goal"
                type="number"
                min="0"
                step="1"
                value={monthlySavingsGoal}
                onChange={(e) => setMonthlySavingsGoal(e.target.value)}
                placeholder="e.g. 20000"
              />
            </div>
            <p className="text-xs text-[var(--fl-text-2)]">
              Spending on yearly budgets isn&rsquo;t counted in the monthly budget.
            </p>
          </section>
        )}

        <section>
          <h3 className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--fl-text-2)]">
            Spending categories
          </h3>
          {categoriesData && spending.length === 0 && (
            <EmptyState
              compact
              icon={Tags}
              title="No spending categories yet"
              description="Add spending categories and you can give each one a monthly or yearly budget here."
            />
          )}
          <div className="divide-y divide-[var(--fl-divider)]">
            {spending.map((cat) => {
              const r = row(cat)
              return (
                <div key={cat.id} className="flex items-center gap-2.5 py-2.5">
                  <CategoryIcon
                    icon={cat.icon}
                    size={13}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--fl-text-1)]">
                    {cat.name}
                  </span>
                  <PeriodSwitch
                    label={`${cat.name} budget period`}
                    value={r.period}
                    onChange={(period) => edit(cat, { period })}
                  />
                  <Input
                    aria-label={`${cat.name} budget (${symbol})`}
                    type="number"
                    min="0"
                    step="1"
                    value={r.amount}
                    onChange={(e) => edit(cat, { amount: e.target.value })}
                    placeholder="No budget"
                    className="w-24 px-2 py-1.5 text-right"
                  />
                </div>
              )
            })}
          </div>
          {spending.length > 0 && (
            <p className="mt-2 text-xs text-[var(--fl-text-2)]">
              Yearly budgets run with your financial year ({fyMonths}).
            </p>
          )}
        </section>

        <p className="rounded-xl bg-[var(--fl-surface-subtle)] px-3 py-2.5 text-xs text-[var(--fl-text-2)]">
          Income categories don&rsquo;t take a budget.
        </p>
      </div>

      <div className="border-t border-[var(--fl-divider)] px-4 py-3">
        {mutation.isError && (
          <p role="alert" className="mb-2 text-xs text-[var(--fl-error)]">
            Couldn&rsquo;t save the budget. Please try again.
          </p>
        )}
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="flex-1"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </FormDialog>
  )
}
